/**
 * Podium scene — end-of-game celebration. Top 5 players ranked, podium-bar
 * heights for ranks 1-3 (visual flair).
 */

import type { Podium } from '../state/types.js';

export interface PodiumHandle {
  el: HTMLDivElement;
  setPodium(p: Podium[]): void;
  setVisible(v: boolean): void;
}

export function createPodium(stage: HTMLElement): PodiumHandle {
  const el = document.createElement('div');
  el.setAttribute('data-scene', 'podium');
  el.setAttribute('data-testid', 'ss-podium');
  el.style.cssText = `
    position: absolute; inset: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Oswald', sans-serif;
    padding: 80px;
    background: radial-gradient(ellipse at center, var(--sg-secondary, #060CE9) 0%, var(--sg-bg, #0A0E27) 100%);
  `;

  const title = document.createElement('div');
  title.textContent = 'GAME OVER';
  title.style.cssText = `
    font-family: 'Bebas Neue', sans-serif; font-size: 160px; letter-spacing: 12px;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 8px 0 var(--sg-secondary, #060CE9);
  `;

  const list = document.createElement('div');
  list.style.cssText = `
    margin-top: 60px;
    display: flex; flex-direction: column; gap: 18px;
    min-width: 720px;
  `;
  list.setAttribute('data-testid', 'ss-podium-list');

  el.appendChild(title);
  el.appendChild(list);
  stage.appendChild(el);

  return {
    el,
    setPodium(rows) {
      list.innerHTML = '';
      if (rows.length === 0) {
        const empty = document.createElement('div');
        empty.textContent = 'No scores this game.';
        empty.style.cssText = 'opacity: 0.7; font-size: 32px; text-align: center;';
        list.appendChild(empty);
        return;
      }
      for (const r of rows) {
        const row = document.createElement('div');
        const isTop = r.rank === 1;
        row.style.cssText = `
          display: flex; align-items: center; justify-content: space-between;
          padding: ${isTop ? '24px 32px' : '16px 28px'};
          font-size: ${isTop ? 56 : 36}px;
          background: ${isTop ? 'var(--sg-celebration, #22C55E)' : 'rgba(255,196,61,0.18)'};
          border: 3px solid var(--sg-primary, #FFC43D);
          border-radius: 14px;
        `;
        const left = document.createElement('span');
        left.textContent = `#${r.rank} ${r.display_name}`;
        const right = document.createElement('span');
        right.textContent = `${r.total_points} pts`;
        right.style.cssText = 'font-weight: 700;';
        row.appendChild(left);
        row.appendChild(right);
        list.appendChild(row);
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
  };
}
